import { Component, HostBinding, OnDestroy, OnInit, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, auditTime, combineLatest, distinctUntilChanged, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { BrowserService } from 'app/modules/social/services/browser.service';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import User from 'entity/User';
import { BaseUser } from 'messages/websocket.messages';

import { GalleryService } from '../../services/gallery/gallery.service';
import { SceneService } from '../../services/scene.service';
import { socialAppSelectors } from '../../state/app';

const MAX_SPEAKING_PARTICIPANTS = 4;

@Component({
  selector: 'app-presentation-speakers',
  templateUrl: './presentation-speakers.component.html',
  styleUrls: ['./presentation-speakers.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationSpeakersComponent extends Loggable implements OnDestroy, OnInit {
  private store = inject(Store);
  protected galleryService = inject(GalleryService);
  private sceneService = inject(SceneService);
  private browserService = inject(BrowserService);

  private readonly speakingParticipants = signal<User[]>([]);
  public readonly toShowSpeakingParticipants = signal<User[]>([]);
  protected readonly newVisualIndicators = signal(false);
  private readonly activeSpeakersUsers = signal<User[]>([]);
  private readonly baseUsersList = signal<BaseUser[]>([]);
  protected isNoAudienceParticipationActive = false;
  protected readonly isSinglePushToSpeak = signal(false);
  public readonly isMobile = signal(false);

  public actualSpeakingIds: string[] = [];
  public readonly toApplySize = signal(MAX_SPEAKING_PARTICIPANTS);
  @HostBinding('class.has-speakers') protected hasSpeakersParticipants = false;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.visual_indicators)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newVisualIndicators) => {
        this.newVisualIndicators.set(newVisualIndicators);
      });

    combineLatest([
      this.store.select(socialAppSelectors.getActiveSpeakers).pipe(auditTime(100), distinctUntilChanged()),
      this.store.select(socialAppSelectors.getSpeakingParticipantValues).pipe(auditTime(100), distinctUntilChanged()),
      this.store.select(socialAppSelectors.isOpenModeActive).pipe(distinctUntilChanged()),
      this.store.select(socialAppSelectors.isSinglePushToTalkActive).pipe(distinctUntilChanged()),
      this.galleryService.allBaseUsers$.pipe(auditTime(100), distinctUntilChanged()),
      this.store.select(socialAppSelectors.getPresenterIds).pipe(distinctUntilChanged()),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        ([
          activeSpeakers,
          speakingParticipants,
          isOpenModeActive,
          isSinglePushToTalkActive,
          baseUsersList,
          presenters,
        ]) => {
          this.baseUsersList.set(baseUsersList);
          this.isSinglePushToSpeak.set(isSinglePushToTalkActive);

          if (isOpenModeActive) {
            const newActiveSpeaker = this.removePresenter([...presenters], [...activeSpeakers]);
            this.getActiveSpeakersUser(newActiveSpeaker);
            this.speakingParticipants.set(this.activeSpeakersUsers());
            this.organizeParticipants();
            return;
          }

          if (isSinglePushToTalkActive) {
            this.speakingParticipants.set(speakingParticipants.length ? [speakingParticipants[0]] : []);
            this.organizeParticipants();
            return;
          }

          this.speakingParticipants.set(speakingParticipants);
          this.organizeParticipants();
        },
      );
  }

  public ngOnInit(): void {
    this.store
      .select(socialAppSelectors.isNoAudienceParticipationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (isNoAudienceParticipationActive) => (this.hasSpeakersParticipants = !isNoAudienceParticipationActive),
      );

    this.isMobile.set(this.browserService.isMobile());
  }

  public organizeParticipants(size?: number): void {
    // limit to the allowed by the height of the container (maximum 4)
    let auxToApplySize = size || MAX_SPEAKING_PARTICIPANTS;
    if (size && size > MAX_SPEAKING_PARTICIPANTS) {
      auxToApplySize = MAX_SPEAKING_PARTICIPANTS;
    }

    if (this.isSinglePushToSpeak()) {
      auxToApplySize = 1;
    }
    // Minimum 1
    const newApplySize = auxToApplySize > 0 ? auxToApplySize : 1;
    this.toApplySize.set(newApplySize);

    const removedParticipants = this.getUsersRemovedFromSpeaking(this.actualSpeakingIds, this.speakingParticipants());
    const addedParticipants = this.getUsersAddedToSpeaking(this.actualSpeakingIds, this.speakingParticipants());

    if (removedParticipants.length) {
      this.toShowSpeakingParticipants.update((p) =>
        p.filter((user) => !removedParticipants.includes(user.userId || '')),
      );
    }
    if (addedParticipants.length) {
      addedParticipants.forEach((toAddId) => {
        const index = this.speakingParticipants().findIndex((user) => user.userId === toAddId);
        this.toShowSpeakingParticipants.update((p) => [this.speakingParticipants()[index], ...p]);
      });
    }
    this.actualSpeakingIds = this.speakingParticipants()
      .map((user) => user.userId)
      .filter(notNil);
  }

  public getUsersRemovedFromSpeaking = (prev: string[], current: User[]): string[] => {
    const prevIds = prev.filter(notNil);
    const currentIds = current.map((u) => u.userId).filter(notNil);
    return prevIds.filter((p) => !currentIds.includes(p));
  };

  public getUsersAddedToSpeaking = (prev: string[], current: User[]): string[] => {
    const prevIds = prev.filter(notNil);
    const currentIds = current.map((u) => u.userId).filter(notNil);
    return currentIds.filter((p) => !prevIds.includes(p));
  };

  public ngOnDestroy(): void {
    this.galleryService.destroy();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public getParticipantRingColor(userId: string | undefined): string | undefined {
    if (!userId) {
      return;
    }
    // if is a cross room it will get the ring color from a baseUser
    return (
      this.baseUsersList().find((user) => user.userId === userId)?.ringColor || this.galleryService.getRingColor(userId)
    );
  }

  private removePresenter(presenters: string[], activeSpeakers: string[]): string[] {
    return activeSpeakers.filter((speaker) => !presenters.includes(speaker));
  }

  private getActiveSpeakersUser(activeSpeaker: string[]): void {
    this.activeSpeakersUsers.set([]);
    const usersList = this.baseUsersList().length ? this.baseUsersList() : this.sceneService.getAllUsers();
    activeSpeaker.forEach((userId) => {
      const speakingUser = usersList.find((user) => user.userId === userId);
      if (speakingUser) {
        this.activeSpeakersUsers.update((p) => [...p, speakingUser]);
      }
    });
  }
}
